

















































































































































































import { Component, Prop, Watch, Vue, Inject } from "@/shared/vuepack";
import { Validator } from "vee-validate";
import { Location } from "vue-router";
import { PartnerNewEmployer, EmployerHome } from "@/employer/route-names";
import {
  PartnerEmployer,
  PartnerEmployerStatus,
  CensusManager,
  PartnerEmployersSearchFields,
  PartnerEmployerListSortOption
} from "@/employer/types";
import Pagination from "@/shared/components/ui/Pagination.vue";
import PageSizeSelectList from "@/shared/components/ui/PageSizeSelectList.vue";
import EnrollmentStatus from "@/employer/components/shared/EnrollmentStatus.vue";
import { FundingOption, SortOrder, UserRoleNames } from "@/shared/types";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useUserStore } from "@/shared/store/user";
import { usePartnerStore } from "@/employer/store/partner";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useRoleStore } from "@/shared/store/role";


const initialSearchTerms: PartnerEmployersSearchFields = {
  employerName: "",
  employerId: "",
  taxId: "",
  externalEmployerId: "",
};

@Component({
  components: {
    Pagination,
    EnrollmentStatus,
    PageSizeSelectList,
    GenericLoadingSpinner
  }
})
export default class PartnerHome extends Vue {
  @Inject()
  $validator: Validator;

  userStore = useUserStore();
  partnerStore = usePartnerStore();
  pageSizeStore = usePageSizeStore();
  featureFlagStore = useFeatureFlagStore();
  roleStore = useRoleStore();

  CensusManager = CensusManager;
  PartnerEmployerListSortOption = PartnerEmployerListSortOption;

  pageNumber = 1;
  searchTerm = { ...initialSearchTerms };
  sortOption = PartnerEmployerListSortOption.Name;
  sortOrder = SortOrder.Ascending;
  isSubmitting = false;

  @Prop()
  partnerId: number;

  @Prop()
  routeToNewEmployee: () => Location;

  get displayEmptyResultsMessage (): boolean {
    return this.partnerStore.employersLoaded() && this.partnerEmployersCount === 0;
  }

  get partnerEmployers (): PartnerEmployer[] {
    return this.partnerStore.employers();
  }

  get partnerEmployersCount (): number {
    return this.partnerStore.totalRecords;
  }

  get showAddEmployerGroupButton () {
    return this.userStore.currentPartner && this.userStore.currentPartner.partnerUserRoles && this.roleStore.hasPartnerRoles &&
      this.userStore.currentPartner.partnerUserRoles.includes(this.roleStore.partnerRoles.find(r => r.roleName === UserRoleNames.partnerAdmin).roleId);
  }

  get companyName () {
    return this.userStore.currentPartner && this.userStore.currentPartner.companyName;
  }

  get showSpinner (): boolean {
    return this.userStore.loggedIn && this.userStore.currentPartner === null;
  }

  get pageCount () {
    return Math.ceil(this.partnerEmployersCount / this.pageSizeStore.pageSize);
  }

  get canEditEmployer () {
    // when showfundingmethod is true user can edit funding method
    // When showBulkEnrollment is true user can edit enrollmentOption
    return this.featureFlagStore.flags.showFundingMethod || this.featureFlagStore.flags.showBulkEnrollment;
  }

  get defaultSortOption () {
    return this.featureFlagStore.flags.bend15691OrderByRelevance
      ? PartnerEmployerListSortOption.None // sort be relevance
      : PartnerEmployerListSortOption.Name;
  }

  get sortedAscending (): boolean {
    return this.sortOrder === SortOrder.Ascending;
  }

  get externalIdSorted (): boolean {
    return this.sortOption === PartnerEmployerListSortOption.ExternalEmployerId;
  }

  get nameSorted (): boolean {
    return this.sortOption === PartnerEmployerListSortOption.Name;
  }

  get enrolledSorted (): boolean {
    return this.sortOption === PartnerEmployerListSortOption.Enrolled;
  }

  get censusPartnerSorted (): boolean {
    return this.sortOption === PartnerEmployerListSortOption.CensusPartner;
  }

  get isFormValid () {
    return !this.$validator.errors.any();
  }

  get isSearchDisabled () {
    return this.isSubmitting === true || !this.isFormValid;
  }

  get isClearDisabled () {
    return this.isSubmitting === true;
  }

  editEmployer (employerId: number, employerName: string) {
    return {
      name: PartnerNewEmployer, params: { partnerId: this.partnerId, step: 1, employerId, edit: true, employerName },
      query: { edit: true }
    };
  }

  routeToNewEmployer () {
    return { name: PartnerNewEmployer, params: { partnerId: this.partnerId, employerId: 0 } };
  }

  routeToEmployerHome (employer: PartnerEmployer) {
    return { name: EmployerHome, params: { employerId: employer.employerId } };
  }

  mounted (): void {
    this.clearSearch();
    this.roleStore.getPartnerRoles();
  }

  gotoNewEmployee (e): void {
    e.preventDefault();
    this.$router.push(this.routeToNewEmployee());
  }

  @Watch("partnerId")
  onPartnerIdChanged () {
    this.clearSearch();
  }

  onSearch (resetPageNumber = false) {
    if (this.isSubmitting || !this.userStore.loggedIn) return;
    if (resetPageNumber) {
      this.pageNumber = 1;
    }
    this.isSubmitting = true;
    return this.partnerStore.searchEmployers({
      partnerId: this.partnerId,
      pageNumber: this.pageNumber,
      pageSize: this.pageSizeStore.pageSize,
      sortOption: this.sortOption,
      sortOrder: this.sortOrder,
      searchTerm: this.searchTerm
    }).finally(() => {
      this.isSubmitting = false;
    });
  }

  switchPageSize (): void {
    // on switching page size, reset to page 1
    this.onSearch(true);
  }

  switchPage (newPageNumber): void {
    this.pageNumber = newPageNumber;
    this.onSearch();
  }

  clearSearch (): void {
    this.searchTerm = { ...initialSearchTerms };
    this.sortOption = this.defaultSortOption;
    this.sortOrder = SortOrder.Ascending;
    this.onSearch(true);
  }

  selectSortOption (sortOption: PartnerEmployerListSortOption): void {
    // on switching the sorting, reset to page 1
    if (sortOption === this.sortOption) {
      this.sortOrder = this.sortOrder === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending;
    }
    this.sortOption = sortOption;

    this.onSearch(true);
  }

  employerAbbr (employer: PartnerEmployer): string {
    let abbr = "";
    const pieces = employer.name.split(/\W/);

    for (const piece of pieces) {
      if (!piece) {
        continue;
      }

      if (abbr.length >= 2) {
        break;
      }

      abbr += piece[0];
    }

    return abbr.toLocaleUpperCase();
  }

  fundingOptionDescriptionText (fundingOption: FundingOption): string {
    switch (fundingOption) {
      case FundingOption.BendCalculatePull:
        return "Automated";
      case FundingOption.ErCalculateBendPull:
        return "File Upload";
      case FundingOption.ErCalculateErPush:
        return "Direct Deposit";
      default:
        return "None";
    }
  }

  statusText (employer: PartnerEmployer): string {
    if (employer.isTerminated) {
      return "Terminated";
    }

    switch (employer.status) {
      case PartnerEmployerStatus.Suspended:
        return "Suspended";
      case PartnerEmployerStatus.UmbAccountCreationComplete:
      case PartnerEmployerStatus.EmployeeInviteEmailComplete:
        return "Active";
      default:
        return "Pending";
    }
  }

  statusType (employer: PartnerEmployer): string {
    if (employer.isTerminated) {
      return "blocked";
    }

    switch (employer.status) {
      case PartnerEmployerStatus.Suspended:
        return "blocked";
      case PartnerEmployerStatus.UmbAccountCreationComplete:
      case PartnerEmployerStatus.EmployeeInviteEmailComplete:
        return "enrolled";
      default:
        return "ineligible";
    }
  }
}
